import noop from "lodash/noop";
import PropTypes from "prop-types";
import React from "react";
import classNames from "classnames";
import {useTranslation} from "@unibuddy/intl";
import {Badge} from "@unibuddy/patron";

import {getFormattedDate, getLocalTime} from "shared/Utils/DateUtils";
import {Flag} from "../../../Buddies/components/Flag/Flag";
import MessageCreated from "../../../../widgets/modules/Chat/components/MessageCreated/MessageCreated";
import ContentButton from "../../../General/components/ContentButton/ContentButton";
import s from "./InboxItem.pcss";
import InitialAvatar from "../../../Applicant/components/InitialAvatar/InitialAvatar";
import {getMentorInboxInfoOne} from "../util/InboxInfo";
import {getenv} from "../../../../util/getEnv";
import {titleCase} from "../../../../util/titleCase";
import {InboxItemAriaText} from "./InboxItemAriaText";
import {IntlNamespaces} from "../../../../translations/types";
import {INTL_NO_LAST_MESSAGE} from "../../../../translations/inbox";

const InboxItem = (props) => {
    const {t} = useTranslation(IntlNamespaces.INBOX);
    const {user, message, className} = props;
    const countryCode = user && user.countryCode;
    const onlineStatus = props.showOnlineStatus ? (
        <span className={user.isOnline ? s.online : s.offline} />
    ) : null;
    const mentorInboxInfoOne =
        !props.isExternal && getMentorInboxInfoOne({sender: user, university: props.university, t});
    const DEFAULT_PROFILE_PHOTO = getenv("DEFAULT_PROFILE_PHOTO");
    const noLastMessage = t(INTL_NO_LAST_MESSAGE.key, INTL_NO_LAST_MESSAGE.defaultValue);

    return user ? (
        <>
            <InboxItemAriaText
                id={props.id}
                name={user.firstName}
                role={user.accountRole}
                degreeTitle={mentorInboxInfoOne || user.inboxInfoOne}
                creationSource={props.creationSource?.name}
                unreadMessageCount={props.unread}
                lastMessage={(message?.text ?? "") || noLastMessage}
                date={getFormattedDate(message?.created)}
                time={getLocalTime(message?.created).format("LT")}
            />
            <ContentButton
                className={classNames(s.inboxItem, className)}
                onClick={props.onClick}
                aria-labelledby={`inbox-item-desc-${props?.id}`}
            >
                <div className={s.profileContainer} data-test-id={props.dataTestId}>
                    {!user.profilePhoto || user.profilePhoto === DEFAULT_PROFILE_PHOTO ? (
                        <InitialAvatar
                            className={user.profilePhoto}
                            name={user.firstName}
                            round
                            size={40}
                        />
                    ) : (
                        <img src={user.profilePhoto} alt="" />
                    )}
                    {onlineStatus}{" "}
                    {/* We don't need this anymore. Since showOnlineStatus is always false. It's being set from ApplicantInbox.
                    If we are showing this, we should probably get from user.isOnline*/}
                </div>
                <div className={s.inboxItemInfo}>
                    <div>
                        <span>
                            <span className={s.name}>{user.firstName}</span>
                            <span className={s.info}>{` , ${titleCase(user.accountRole)}`}</span>
                            <span className={s.info}>
                                {mentorInboxInfoOne
                                    ? `, ${mentorInboxInfoOne}`
                                    : `, ${user.inboxInfoOne}`}
                            </span>
                            {countryCode ? (
                                <Flag data-test-id="flag" countryCode={countryCode} />
                            ) : null}

                            {props.creationSource?.type === "marketplace" && (
                                <Badge
                                    className={s.sourceBadge}
                                    color="violet"
                                    size="standard"
                                    elevation={0}
                                >
                                    {props.creationSource?.name}
                                </Badge>
                            )}
                        </span>
                        {props.unread > 0 && (
                            <span data-test-id={"unread-count"} className={s.status}>
                                {props.unread}
                            </span>
                        )}
                    </div>
                    <div>
                        <span className={s.message}>
                            {(message?.text ?? "") || <i>{noLastMessage}</i>}
                        </span>
                        <span className={s.created} data-test-id="conversation-timestamp">
                            <MessageCreated timestamp={message?.created} dateVisible alt />
                        </span>
                    </div>
                </div>
            </ContentButton>
        </>
    ) : null;
};

InboxItem.propTypes = {
    id: PropTypes.string,
    dataTestId: PropTypes.string,
    creationSource: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        slug: PropTypes.string,
        type: PropTypes.string,
    }),
    message: PropTypes.shape({
        text: PropTypes.string,
        created: PropTypes.string,
    }).isRequired,
    university: PropTypes.object,
    unread: PropTypes.number.isRequired,
    user: PropTypes.shape({
        firstName: PropTypes.string.isRequired,
        inboxInfoOne: PropTypes.string,
        isOnline: PropTypes.bool.isRequired,
        profilePhoto: PropTypes.string,
    }).isRequired,
    showOnlineStatus: PropTypes.bool.isRequired,
    onClick: PropTypes.func,
    className: PropTypes.string,
    isExternal: PropTypes.bool,
};

InboxItem.defaultProps = {
    onClick: noop,
    className: "",
};

export default InboxItem;
